import type { ReactNode } from 'react';
import { Star } from 'lucide-react';
import { StatusPill, type PillTone } from './StatusPill';

/** Kit `Stars` — red ≤2, amber 3, olive 4–5; unfilled stars at .22. */
export function Stars({ n, label }: { n: number; label?: string }) {
  const color = n <= 2 ? 'text-[#B23A2E]' : n === 3 ? 'text-[#7A5A12]' : 'text-[#B49E31]';
  return (
    <span className={`inline-flex gap-px ${color}`} role="img" aria-label={label ?? `${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={13}
          strokeWidth={2}
          aria-hidden="true"
          className={i <= n ? 'fill-current' : 'opacity-[.22]'}
        />
      ))}
    </span>
  );
}

/** Kit `LevelDot` — locale activity level (BE `activityLevel`). */
export type ActivityLevel = 'high' | 'medium' | 'low';

export function LevelDot({ level }: { level: ActivityLevel }) {
  const bg = { high: 'bg-green', medium: 'bg-olive', low: 'bg-danger' }[level];
  return (
    <span
      title={level}
      aria-label={`${level} activity`}
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${bg}`}
    />
  );
}

/**
 * The BE's resolution source for a setting (BE reqs §7.4 getter helpers).
 * Handoff rule: `unset` and `0` are different states — always show the
 * effective value WITH where it came from.
 */
export type SettingSource = 'city_override' | 'locale_default' | 'config_default';

const SOURCE: Record<SettingSource, { label: string; tone: PillTone }> = {
  city_override: { label: 'city override', tone: 'info' },
  locale_default: { label: 'locale', tone: 'locale' },
  config_default: { label: 'default', tone: 'neutral' },
};

export function EffectiveValue({
  value,
  source,
}: {
  value: ReactNode;
  source: SettingSource | string | null | undefined;
}) {
  const s = source && source in SOURCE ? SOURCE[source as SettingSource] : undefined;
  return (
    <span className="inline-flex items-center gap-[7px] whitespace-nowrap">
      <span className="font-semibold tabular-nums">{value}</span>
      <StatusPill tone={s?.tone ?? 'neutral'}>{s?.label ?? source ?? 'unknown source'}</StatusPill>
    </span>
  );
}

/**
 * Per-city tax coverage (BE `coverage`), the Locale Health screen's key
 * element: fully_covered green / state_only amber / uncollected red. State
 * only is a gap in a home rule city (its own tax goes uncollected), so it is
 * red there.
 */
export type Coverage = 'fully_covered' | 'state_only' | 'uncollected';

const COVERAGE: Record<Coverage, { label: string; tone: PillTone }> = {
  fully_covered: { label: 'Fully covered', tone: 'success' },
  state_only: { label: 'State only', tone: 'warning' },
  uncollected: { label: 'Uncollected', tone: 'danger' },
};

export function CoverageBadge({ coverage, homeRule = false }: { coverage: Coverage | string; homeRule?: boolean }) {
  const c = COVERAGE[coverage as Coverage];
  const tone = homeRule && coverage === 'state_only' ? 'danger' : (c?.tone ?? 'neutral');
  return (
    <StatusPill tone={tone} dot>
      {c?.label ?? coverage}
    </StatusPill>
  );
}
